import { useState, useEffect } from "react";
import { api } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, Plus, Edit, Trash2, Download, Building2, Receipt, User, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatNumber } from "../utils/formatters";

const FinancialsPage = () => {
  const [financials, setFinancials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    project_name: "",
    revenue: "",
    size_sqft: "",
    units: "",
    measurement_type: "sqft",
    status: "active",
  });

  const [expenseFormData, setExpenseFormData] = useState({
    project_name: "",
    expense_type: "supplies",
    amount: "",
    description: "",
    expense_date: "",
  });

  const [adjustmentForm, setAdjustmentForm] = useState({
    project_id: '',
    employee_id: '',
    type: 'Employee',
    amount: '',
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [financialsRes, projectsRes, expensesRes, summaryRes, adjustmentsRes, employeesRes, timeEntriesRes] = await Promise.all([
        api.get("/financials/projects"),
        api.get("/projects"),
        api.get("/expenses"),
        api.get("/financials/summary"),
        api.get("/pay-adjustments"),
        api.get("/employees"),
        api.get("/time-entries"),
      ]);
      setFinancials(financialsRes.data);
      setProjects(projectsRes.data);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setAdjustments(adjustmentsRes.data);
      setEmployees(employeesRes.data);
      setTimeEntries(timeEntriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch financial data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        project_name: project.project_name,
        revenue: project.revenue.toString(),
        size_sqft: project.size_sqft?.toString() || "",
        units: project.units?.toString() || "",
        measurement_type: project.measurement_type,
        status: project.status,
      });
    } else {
      setEditingProject(null);
      setFormData({
        project_name: "",
        revenue: "",
        size_sqft: "",
        units: "",
        measurement_type: "sqft",
        status: "active",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        project_name: formData.project_name,
        revenue: parseFloat(formData.revenue),
        size_sqft: formData.size_sqft ? parseFloat(formData.size_sqft) : null,
        units: formData.units ? parseInt(formData.units) : null,
        measurement_type: formData.measurement_type,
        status: formData.status,
      };

      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, payload);
        toast.success("Project updated successfully");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created successfully");
      }

      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error("Failed to save project");
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Project deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  const getMarginColor = (margin) => {
    if (margin >= 30) return "text-emerald-600 bg-emerald-50";
    if (margin >= 20) return "text-green-600 bg-green-50";
    if (margin >= 10) return "text-yellow-600 bg-yellow-50";
    if (margin >= 0) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  const getMarginBadge = (margin) => {
    if (margin >= 30) return { label: "Excellent", color: "bg-emerald-500" };
    if (margin >= 20) return { label: "Good", color: "bg-green-500" };
    if (margin >= 10) return { label: "Fair", color: "bg-yellow-500" };
    if (margin >= 0) return { label: "Low", color: "bg-orange-500" };
    return { label: "Loss", color: "bg-red-500" };
  };

  const exportToCSV = () => {
    const headers = [
      "Project",
      "Revenue",
      "Labor Cost",
      "Profit",
      "Margin %",
      "Size (sqft)",
      "Units",
      "$/sqft (Revenue)",
      "$/sqft (Cost)",
      "$/sqft (Profit)",
      "$/unit (Revenue)",
      "$/unit (Cost)",
      "$/unit (Profit)",
      "Hours",
      "Employees",
      "Status"
    ];

    const rows = financials.map(f => [
      f.project_name,
      f.revenue,
      f.labor_cost,
      f.profit,
      f.margin_percentage,
      f.size_sqft || "N/A",
      f.units || "N/A",
      f.revenue_per_sqft || "N/A",
      f.cost_per_sqft || "N/A",
      f.profit_per_sqft || "N/A",
      f.revenue_per_unit || "N/A",
      f.cost_per_unit || "N/A",
      f.profit_per_unit || "N/A",
      f.total_hours,
      f.employee_count,
      f.status
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `financials_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success("Financial report exported successfully");
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalRevenueFromProjects = projects.reduce((sum, p) => sum + (p.revenue || 0), 0);
  const totalRevenue = financials.reduce((sum, f) => sum + f.revenue, 0);
  const totalCost = financials.reduce((sum, f) => sum + f.labor_cost, 0);
  const totalExpenses = financials.reduce((sum, f) => sum + f.expenses, 0);
  const totalProfit = financials.reduce((sum, f) => sum + f.profit, 0);
  const avgMargin = financials.length > 0 
    ? financials.reduce((sum, f) => sum + f.margin_percentage, 0) / financials.length
    : 0;

  // Calculate Owner Earnings and Employee Labor Cost from time entries
  const employeeLookup = employees.reduce((acc, emp) => {
    acc[emp.id] = emp;
    return acc;
  }, {});

  let ownerEarnings = 0;
  let employeeLaborCost = 0;

  timeEntries.forEach(entry => {
    const amount = entry.amount || (entry.hours * entry.hourly_pay_rate) || 0;
    const employee = employeeLookup[entry.employee_id];
    
    if (employee && employee.type === 'Owner') {
      ownerEarnings += amount;
    } else {
      // Default to Employee if no employee or type not specified
      employeeLaborCost += amount;
    }
  });

  const lowMarginProjects = financials.filter(f => f.margin_percentage < 15).length;

  return (
    <div className="space-y-6 page-enter" data-testid="financials-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Financial Analytics</h1>
          <p className="text-slate-600 mt-1">Revenue, costs, and profitability analysis</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="border-slate-300"
            data-testid="export-financials-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards - Redesigned for better visual hierarchy */}
      <div className="space-y-6 mb-8">
        {/* Top Row - Primary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-500 mb-2">Total Revenue</p>
                  <p className="text-3xl font-bold text-slate-900 mb-2">
                    {formatCurrency(totalRevenueFromProjects)}
                  </p>
                  <p className="text-xs text-slate-400">From {projects.length} projects</p>
                </div>
                <div className="bg-blue-100 p-4 rounded-xl">
                  <DollarSign className="h-7 w-7 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-500 mb-2">Total Profit</p>
                  <p className="text-3xl font-bold text-emerald-600 mb-2">
                    {formatCurrency(totalProfit)}
                  </p>
                  <p className="text-xs text-slate-400">Net earnings</p>
                </div>
                <div className="bg-emerald-100 p-4 rounded-xl">
                  <TrendingUp className="h-7 w-7 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-500 mb-2">Avg Margin</p>
                  <p className="text-3xl font-bold text-slate-900 mb-2">
                    {avgMargin.toFixed(1)}%
                  </p>
                  <p className="text-xs text-slate-400">Profit margin</p>
                </div>
                <div className="bg-slate-100 p-4 rounded-xl">
                  <Building2 className="h-7 w-7 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row - Cost Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-blue-200 bg-blue-50/50 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-700 mb-2">Owner Earnings</p>
                  <p className="text-3xl font-bold text-blue-800 mb-2">
                    {formatCurrency(ownerEarnings)}
                  </p>
                  <p className="text-xs text-blue-600">From time entries</p>
                </div>
                <div className="bg-blue-200 p-4 rounded-xl">
                  <User className="h-7 w-7 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-700 mb-2">Labor Cost</p>
                  <p className="text-3xl font-bold text-purple-800 mb-2">
                    {formatCurrency(employeeLaborCost)}
                  </p>
                  <p className="text-xs text-purple-600">Employee time entries</p>
                </div>
                <div className="bg-purple-200 p-4 rounded-xl">
                  <Users className="h-7 w-7 text-purple-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50/50 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-700 mb-2">Expenses</p>
                  <p className="text-3xl font-bold text-orange-800 mb-2">
                    {formatCurrency(totalExpenses)}
                  </p>
                  <p className="text-xs text-orange-600">Total costs</p>
                </div>
                <div className="bg-orange-200 p-4 rounded-xl">
                  <Receipt className="h-7 w-7 text-orange-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Alerts */}
      {lowMarginProjects > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-semibold text-orange-900">
                  Low Margin Alert
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  {lowMarginProjects} project{lowMarginProjects !== 1 ? 's' : ''} have margins below 15%. 
                  Review pricing or reduce labor costs.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Table with Margin Heatmap */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Project Financials</CardTitle>
          <CardDescription>Comprehensive financial breakdown with margin heatmap</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Labor</TableHead>
                  <TableHead>Expenses</TableHead>
                  <TableHead>Profit</TableHead>
                  <TableHead>Margin</TableHead>
                  <TableHead>$/sqft</TableHead>
                  <TableHead>Quantity/Area</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {financials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                      No financial data available. Add project revenue details to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  financials.map((financial, index) => {
                    const marginBadge = getMarginBadge(financial.margin_percentage);
                    const project = projects.find(p => p.project_name === financial.project_name);
                    
                    // Calculate owner and employee labor for this specific project
                    const projectTimeEntries = timeEntries.filter(entry => 
                      entry.project_name === financial.project_name
                    );
                    
                    let projectOwnerCost = 0;
                    let projectEmployeeCost = 0;
                    
                    projectTimeEntries.forEach(entry => {
                      const amount = entry.amount || (entry.hours * entry.hourly_pay_rate) || 0;
                      const employee = employeeLookup[entry.employee_id];
                      
                      if (employee && employee.type === 'Owner') {
                        projectOwnerCost += amount;
                      } else {
                        projectEmployeeCost += amount;
                      }
                    });
                    
                    return (
                      <TableRow key={index} data-testid={`financial-row-${index}`}>
                        <TableCell className="font-medium">{financial.project_name}</TableCell>
                        <TableCell className="text-slate-900 font-semibold">
                          {formatCurrency(financial.revenue)}
                        </TableCell>
                        <TableCell className="text-blue-600">
                          {formatCurrency(projectOwnerCost)}
                        </TableCell>
                        <TableCell className="text-purple-600">
                          {formatCurrency(projectEmployeeCost)}
                        </TableCell>
                        <TableCell className="text-orange-600">
                          {formatCurrency(financial.expenses)}
                        </TableCell>
                        <TableCell className={financial.profit >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                          {formatCurrency(financial.profit)}
                        </TableCell>
                        <TableCell>
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${getMarginColor(financial.margin_percentage)}`}>
                            <div className={`w-2 h-2 rounded-full ${marginBadge.color}`}></div>
                            <span className="font-semibold">{financial.margin_percentage.toFixed(1)}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {project?.size_sqft ? (
                            <div className="space-y-1">
                              <div className="text-emerald-600 font-medium">
                                {formatCurrency(financial.revenue / project.size_sqft)}/sqft
                              </div>
                              <div className="text-xs text-slate-500">
                                {formatNumber(project.size_sqft)} sqft
                              </div>
                            </div>
                          ) : "N/A"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {project?.size_sqft || project?.units ? (
                            <div className="text-slate-700">
                              {project.size_sqft ? `${formatNumber(project.size_sqft)} sqft` : `${project.units} units`}
                            </div>
                          ) : "N/A"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {financial.total_hours}h
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            financial.status === "completed" 
                              ? "border-slate-400 text-slate-700"
                              : "border-green-300 bg-green-50 text-green-700"
                          }>
                            {financial.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {project && (
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.location.href = `/projects/${project.id}?tab=expenses`}
                                className="text-[#247C75] hover:text-[#1d6259] hover:bg-[#247C75]/10"
                                title="View Expenses"
                              >
                                <Receipt className="h-4 w-4 mr-1" />
                                <ArrowRight className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenDialog(project)}
                                data-testid={`edit-project-${index}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteProject(project.id, project.project_name)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-project-${index}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="project-financial-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingProject ? "Edit Project Financials" : "Add Project Financials"}
            </DialogTitle>
            <DialogDescription>
              Enter project revenue and size details for financial tracking
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="project_name">Project Name *</Label>
                <Input
                  id="project_name"
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  required
                  disabled={editingProject !== null}
                  data-testid="project-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="revenue">Total Revenue ($) *</Label>
                <Input
                  id="revenue"
                  type="number"
                  step="0.01"
                  value={formData.revenue}
                  onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                  required
                  data-testid="project-revenue-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="measurement_type">Measurement Type *</Label>
                <Select
                  value={formData.measurement_type}
                  onValueChange={(value) => setFormData({ ...formData, measurement_type: value })}
                >
                  <SelectTrigger data-testid="measurement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqft">Square Feet</SelectItem>
                    <SelectItem value="units">Units</SelectItem>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.measurement_type === "sqft" && (
                <div className="space-y-2">
                  <Label htmlFor="size_sqft">Size (Square Feet)</Label>
                  <Input
                    id="size_sqft"
                    type="number"
                    step="0.01"
                    value={formData.size_sqft}
                    onChange={(e) => setFormData({ ...formData, size_sqft: e.target.value })}
                    data-testid="project-sqft-input"
                  />
                </div>
              )}

              {formData.measurement_type === "units" && (
                <div className="space-y-2">
                  <Label htmlFor="units">Number of Units</Label>
                  <Input
                    id="units"
                    type="number"
                    value={formData.units}
                    onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                    data-testid="project-units-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="project-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="project-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="project-save-button"
              >
                {editingProject ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialsPage;
